import { PostNavigation } from "@/components/modules/Post/PostNavigation";
import { getCurrentTheme } from "@/core/store/app/AppSlice";
import { useAppSelector } from "@/core/store/reduxHooks";
import { SidebarHeaderProps } from "@/types/layout";
import autoAnimate from "@formkit/auto-animate";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { AccountNavigation } from "@/components/layout/Sidebar/AccountNavigation";
import { PrimaryNavigation } from "@/components/layout/Sidebar/PrimaryNavigation";
import { SettingsDropdownNavigation } from "@/components/layout/Sidebar/SettingsDropdownNavigation";
import { ReporterInfoNavigation } from "@/components/layout/Sidebar/ReporterInfoNavigation";
import { SecondaryNavigation } from "@/components/layout/Sidebar/SecondaryNavigation";
import classNames from "classnames";
import { secondaryNavigationLinks } from "@/constants/navigation";
import { useAddToHomeScreenPrompt } from "@/hooks/useAddToHomeScreenPrompt";
import useMenusProvider from "@/core/dataProviders/providers/menu/useMenusProvider";
import { pushToDataLayer } from "@/utils/pushToDataLayer";
import useCurrentSubscriptionInfo from "@/hooks/useCurrentSubscriptionInfo";
import { useClickedOutside } from "@/hooks/useClickedOutside";

export const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  openedBurgerMenu,
  postDetailSidebar,
  setOpenedMenu,
  postDetailControls,
  post,
  hideBurgerMenu,
}) => {
  const [openedMenu, setOpenedMenuNav] = useState<boolean>(openedBurgerMenu);
  const currentTheme = useAppSelector(getCurrentTheme);
  const [showScrollbar] = useState<boolean>(false);
  const [prompt, promptToInstall] = useAddToHomeScreenPrompt();
  const [menu] = useMenusProvider();
  const router = useRouter();
  const sidebarRef = useClickedOutside(() => {
    setOpenedMenuNav(false);
  });
  const { getStaticSubscriptionTitle, getStaticSubscriptionURL } = useCurrentSubscriptionInfo();

  const toggleMenu = (value: boolean) => {
    setOpenedMenu?.(value);
    setOpenedMenuNav(value);

    pushToDataLayer({
      event: "article_interaction",
      interaction_type: "click_cta",
      interaction_option: openedMenu ? "close menu" : "open menu",
    });
  };

  useEffect(() => {
    setOpenedMenuNav(openedBurgerMenu);
  }, [openedBurgerMenu]);

  const handleBackButtonClick = () => {
    const storage = globalThis?.sessionStorage;

    if (storage) {
      const prevPath = storage.getItem("prevPath");

      if (prevPath) {
        window.history.back();
      } else {
        router.push("/");
      }
    }

    pushToDataLayer({
      event: "article_interaction",
      interaction_type: "click_cta",
      interaction_option: "back button",
    });
  };

  useEffect(() => {
    sidebarRef.current && autoAnimate(sidebarRef.current);
  }, [sidebarRef]);

  return (
    <div
      ref={sidebarRef}
      className="sticky top-0 z-50 flex w-full items-center justify-between border-b border-gray-200 bg-white dark:border-white-25 dark:bg-dark lg:justify-start"
    >
      {openedMenu && (
        <div
          className={classNames({
            "lg:border-grey-200 fixed bottom-[60px] top-0 z-50 h-screen w-full overflow-y-auto bg-white dark:bg-dark lg:absolute lg:top-[53px] lg:h-[calc(100vh-53px)] lg:w-68 lg:!origin-top lg:border-r lg:shadow-post-sidebar dark:lg:border-white-25 portrait:supports-[height:100dvh]:h-[calc(100dvh-60px)] max-lg:landscape:pl-[62px]":
              openedMenu,
            "scrollbar-content-visible": showScrollbar,
            "scrollbar-content": !showScrollbar,
          })}
        >
          <AccountNavigation postId={post?.id} />
          <PrimaryNavigation navigationItems={menu?.["primary-navigation"]!} hideBurgerMenu={hideBurgerMenu!} />
          <SecondaryNavigation navigationItems={secondaryNavigationLinks} postId={post?.id} />
          <SettingsDropdownNavigation prompt={prompt} promptToInstall={promptToInstall} />
          <ReporterInfoNavigation navigationItems={menu?.["company-navigation"]!} />
        </div>
      )}
      <div className="flex w-full items-center justify-between pt-[2px]">
        <div
          className={`flex items-center justify-between pl-3 lg:w-68 ${
            postDetailSidebar ? "lg:border-r lg:border-gray-200 dark:lg:border-white-25" : ""
          }`}
        >
          <div className="flex items-center py-2">
            {postDetailControls && postDetailSidebar && (
              <button className="mr-4 p-1 lg:hidden" onClick={handleBackButtonClick}>
                <Image
                  src="/assets/images/icons/chevron-left.svg"
                  alt={"Back button"}
                  width={17}
                  height={17}
                  className="dark:invert"
                  style={{
                    width: "17px",
                    height: "17px",
                  }}
                />
              </button>
            )}
            <Link href={"/"}>
              <Image
                src={currentTheme === "dark" ? "/assets/images/logo-dark.svg" : "/assets/images/logo.svg"}
                alt="Logo"
                width={96}
                height={32}
                style={{
                  width: "96px",
                  height: "32px",
                }}
                priority
              />
            </Link>
          </div>
          {postDetailSidebar && (
            <button className="my-auto hidden pr-4 lg:flex" onClick={() => toggleMenu(!openedMenu)}>
              <div className="flex">
                <div className="mr-2 font-sans text-2xs dark:text-white">Menu</div>
                <Image
                  src={openedMenu ? "/assets/images/icons/times.svg" : "/assets/images/icons/bar.svg"}
                  alt="Bar Icon"
                  width={20}
                  height={20}
                  className="dark:invert"
                />
              </div>
            </button>
          )}
        </div>
        <ul className="visible flex items-center pr-4 font-sans text-xs dark:text-white md:text-sm hpXL:invisible">
          <li>
            <Link
              href={getStaticSubscriptionURL(post?.id)}
              onClick={() =>
                pushToDataLayer({
                  event: "article_interaction",
                  interaction_type: "click_cta",
                  interaction_option: "Předplatné",
                })
              }
            >
              {getStaticSubscriptionTitle()}
            </Link>
          </li>
        </ul>
        {postDetailSidebar && postDetailControls && (
          <div className="hidden lg:flex">
            <PostNavigation post={post} />
          </div>
        )}
      </div>
    </div>
  );
};
